import {
  CAMPO_EM_BRANCO, escapeHtml, formatarCep, formatarCpfCnpj, municipioUf, rotuloDocumento
} from './documentosBase';

// Modelos dos documentos complementares (declarações de vistoria/DETRAN). Os requerimentos (ATPV-e,
// 2ª via de CRV e baixa definitiva) ficam no item Requerimentos do Nexus (RequerimentoDetranForm).
// Cada modelo descreve seus campos extras e monta o HTML do documento a partir dos dados do
// cliente e do veículo cadastrados. O HTML sai no mesmo formato da Procuração (folha A4 editável).

const e = escapeHtml;
const preenchido = (v) => v != null && String(v).trim() !== '';
const texto = (v) => (preenchido(v) ? String(v).trim() : '');
const negrito = (v) => `<strong>${e(preenchido(v) ? texto(v) : CAMPO_EM_BRANCO)}</strong>`;

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

// "2026-03-21" → { dia: '21', mes: '03', ano: '2026' }
function partesData(iso) {
  if (!iso) return null;
  const [ano, mes, dia] = iso.split('-');
  return ano && mes && dia ? { dia, mes, ano } : null;
}

export function dataPorExtenso(iso) {
  const p = partesData(iso);
  return p ? `${Number(p.dia)} de ${MESES[Number(p.mes) - 1]} de ${p.ano}` : '____ de ________________ de 20____';
}

export function dataCurta(iso) {
  const p = partesData(iso);
  return p ? `${p.dia}/${p.mes}/${p.ano}` : '____/____/20____';
}

// ── Trechos reutilizados ─────────────────────────────────────────────────────

// "Rua X, Nº10, Casa 2, bairro Centro, CEP 96600-000, Canguçu-RS" — só o que estiver preenchido
export function enderecoCompleto(p) {
  const rua = p.logradouro ? `${p.logradouro}${p.numero ? `, Nº${p.numero}` : ''}` : (p.numero ? `Nº${p.numero}` : '');
  const cep = formatarCep(p.cep);
  const partes = [rua, p.complemento, p.bairro && `bairro ${p.bairro}`, cep && `CEP ${cep}`, municipioUf(p)]
    .map(texto).filter(Boolean);
  return partes.length ? partes.join(', ') : CAMPO_EM_BRANCO;
}

// "portador(a) do RG nº X e do CPF nº Y" (sem o RG quando não cadastrado)
function portador(p) {
  const doc = `${rotuloDocumento(p.cpfCnpj)} nº ${negrito(formatarCpfCnpj(p.cpfCnpj))}`;
  return preenchido(p.rg) ? `portador(a) do RG nº ${negrito(p.rg)} e do ${doc}` : `portador(a) do ${doc}`;
}

// "Eu, NOME, portador(a) do ..., residente e domiciliado(a) em ENDEREÇO"
function qualificacao(p, { nacionalidade = '' } = {}) {
  const nac = preenchido(nacionalidade) ? `, ${e(texto(nacionalidade))}` : '';
  return `Eu, ${negrito(p.nome)}${nac}, ${portador(p)}, residente e domiciliado(a) em ${e(enderecoCompleto(p))}`;
}

// "Fiat Uno, cor branca, ano 2010, de placa ABC1D23, chassi ..., RENAVAM ..."
function veiculoFrase(v, { numeroMotor = '' } = {}) {
  const partes = [negrito(v.marcaModelo)];
  if (preenchido(v.cor)) partes.push(`cor ${e(texto(v.cor))}`);
  if (preenchido(v.ano)) partes.push(`ano ${e(texto(v.ano))}`);
  partes.push(`de placa ${negrito((v.placa || '').toUpperCase())}`);
  partes.push(`chassi ${negrito((v.chassi || '').toUpperCase())}`);
  if (preenchido(v.renavam)) partes.push(`RENAVAM ${e(texto(v.renavam))}`);
  if (preenchido(numeroMotor)) partes.push(`motor nº ${e(texto(numeroMotor))}`);
  return partes.join(', ');
}

// Dados do veículo um embaixo do outro, como no modelo da Procuração
function veiculoLista(v, rotulos, { numeroMotor = '' } = {}) {
  const linhas = [];
  const linha = (rotulo, valor, opcional) => {
    if (opcional && !preenchido(valor)) return;
    linhas.push(`<div><strong>${rotulo}:</strong> ${e(preenchido(valor) ? texto(valor) : CAMPO_EM_BRANCO)}</div>`);
  };
  rotulos.forEach((r) => {
    if (r === 'PLACA') linha('PLACA', (v.placa || '').toUpperCase());
    if (r === 'CHASSI') linha('CHASSI', (v.chassi || '').toUpperCase());
    if (r === 'RENAVAM') linha('RENAVAM', v.renavam, true);
    if (r === 'MODELO') linha('MODELO', v.marcaModelo);
    if (r === 'COR') linha('COR', v.cor, true);
    if (r === 'ANO') linha('ANO', v.ano, true);
    if (r === 'MOTOR') linha('Nº DO MOTOR', numeroMotor);
  });
  return `<div class="veiculo">\n${linhas.join('\n')}\n</div>`;
}

const titulo = (t, longo) => `<h1${longo ? ' class="longo"' : ''}>${t}</h1>`;
const par = (html) => `<p class="par">${html}</p>`;

function localData(ctx) {
  return `${e(texto(ctx.local) || '______________________')}, ${dataPorExtenso(ctx.dataISO)}`;
}

// Data (à esquerda do traço) e legenda da assinatura, no padrão da Procuração
function fechamento(dataHtml, legenda, nota = '') {
  return `<div class="fecha">
<p class="data">${dataHtml}</p>
<div class="linha"></div>
<span>${legenda}</span>${nota ? `\n<p class="nota">${nota}</p>` : ''}
</div>`;
}

// ── Modelos ──────────────────────────────────────────────────────────────────

// Declarações de "não apresentação" de etiqueta/plaqueta/tampa: mesmo texto, muda o item e o motivo.
function declaracaoNaoApresentacao({ id, titulo: nome, sub, objeto, motivo }) {
  return {
    id,
    grupo: 'Declarações',
    titulo: nome,
    sub,
    campos: [],
    gerar: (ctx) => [
      titulo('DECLARAÇÃO'),
      par(`${qualificacao(ctx.declarante)}, declaro ficar civil e criminalmente responsável pela não apresentação ${objeto} do veículo ${veiculoFrase(ctx.veiculo)}, ${motivo}.`),
      fechamento(localData(ctx), 'Declarante')
    ].join('\n')
  };
}

const MOTIVO_EXTRAVIO = 'sendo que a mesma foi extraviada';

// Nº do motor: vem do campo "Nº do motor" do veículo (gravado no cadastro de veículos).
// extras.numeroMotor fica só por compatibilidade com o campo próprio que as declarações tinham antes.
const numeroMotorDo = (ctx) => (ctx.extras || {}).numeroMotor || (ctx.veiculo || {}).motor || '';
const campoNacionalidade = { chave: 'nacionalidade', rotulo: 'Nacionalidade (aparece no documento)', tipo: 'texto', padrao: 'brasileiro' };
const rotulosVeiculo = (comMotor) => ['PLACA', 'CHASSI', 'RENAVAM', 'MODELO', ...(comMotor ? ['MOTOR'] : [])];

// Morfologia: o número do chassi/motor não pode ser conferido com a última vistoria. O VENDEDOR
// assume a responsabilidade desde a data em que comprou o veículo até a data em que o vendeu.
function declaracaoMorfologia({ id, titulo: nome, sub, alvo, comMotor }) {
  return {
    id,
    grupo: 'Declarações',
    titulo: nome,
    sub,
    rotuloDeclarante: 'Vendedor (declarante)',
    campos: [
      { chave: 'dataCompra', rotulo: 'Data em que o vendedor comprou o veículo', tipo: 'data' },
      { chave: 'dataVenda', rotulo: 'Data em que o vendedor vendeu o veículo', tipo: 'data' },
      campoNacionalidade
    ],
    gerar: (ctx) => [
      titulo('DECLARAÇÃO'),
      par(`${qualificacao(ctx.declarante, { nacionalidade: ctx.extras.nacionalidade })}, venho por meio deste declarar que fico civil, criminalmente e administrativamente responsável pela morfologia do número do ${alvo} do veículo abaixo identificado, tendo em vista a impossibilidade de conferência com a última vistoria, desde a data em que o adquiri, ${negrito(ctx.extras.dataCompra ? dataCurta(ctx.extras.dataCompra) : '')}, até a data em que o vendi, ${negrito(ctx.extras.dataVenda ? dataCurta(ctx.extras.dataVenda) : '')}.`),
      veiculoLista(ctx.veiculo, rotulosVeiculo(comMotor), { numeroMotor: numeroMotorDo(ctx) }),
      fechamento(localData(ctx), '(RECONHECER FIRMA VERDADEIRA)')
    ].join('\n')
  };
}

// Remarcação: o número do chassi/motor precisa ser remarcado. Quem assina é o ADQUIRENTE,
// responsável pela remarcação do veículo que está adquirindo.
function declaracaoRemarcacao({ id, titulo: nome, sub, alvo, comMotor }) {
  return {
    id,
    grupo: 'Declarações',
    titulo: nome,
    sub,
    rotuloDeclarante: 'Adquirente (declarante)',
    campos: [campoNacionalidade],
    gerar: (ctx) => [
      titulo('DECLARAÇÃO'),
      par(`${qualificacao(ctx.declarante, { nacionalidade: ctx.extras.nacionalidade })}, venho por meio deste declarar que fico civil, criminalmente e administrativamente responsável pela remarcação do número do ${alvo} do veículo que estou adquirindo.`),
      veiculoLista(ctx.veiculo, rotulosVeiculo(comMotor), { numeroMotor: numeroMotorDo(ctx) }),
      fechamento(localData(ctx), '(RECONHECER FIRMA VERDADEIRA)')
    ].join('\n')
  };
}

export const MODELOS = [
  // ── Declarações ───────────────────────────────────────────────────────────
  declaracaoMorfologia({
    id: 'morfologia-chassi',
    titulo: 'Responsabilidade pela morfologia do chassi',
    sub: 'Vendedor: chassi sem conferência com a última vistoria, da compra até a venda',
    alvo: 'chassi',
    comMotor: false
  }),
  declaracaoMorfologia({
    id: 'morfologia-motor',
    titulo: 'Responsabilidade pela morfologia do motor',
    sub: 'Vendedor: motor sem conferência com a última vistoria, da compra até a venda',
    alvo: 'motor',
    comMotor: true
  }),
  declaracaoRemarcacao({
    id: 'remarcacao-chassi',
    titulo: 'Responsabilidade pela remarcação do chassi',
    sub: 'Adquirente: chassi que precisa ser remarcado, no veículo que está adquirindo',
    alvo: 'chassi',
    comMotor: false
  }),
  declaracaoRemarcacao({
    id: 'remarcacao-motor',
    titulo: 'Responsabilidade pela remarcação do motor',
    sub: 'Adquirente: motor que precisa ser remarcado, no veículo que está adquirindo',
    alvo: 'motor',
    comMotor: true
  }),
  {
    id: 'acesso-numero-motor',
    grupo: 'Declarações',
    titulo: 'Acesso ao número identificador do motor',
    sub: 'Vistoria: remoção/limpeza de componentes (Portaria DETRAN/RS 475/15)',
    campos: [
      { chave: 'dataVistoria', rotulo: 'Data da vistoria', tipo: 'data' },
      { chave: 'horaVistoria', rotulo: 'Horário da vistoria', tipo: 'hora' },
      {
        chave: 'decisao',
        rotulo: 'Remoção/limpeza de componentes',
        tipo: 'select',
        opcoes: [
          { valor: '', rotulo: 'Deixar em branco (marcar à mão)' },
          { valor: 'IREI', rotulo: 'Irei realizar' },
          { valor: 'NAO_IREI', rotulo: 'Não irei realizar' }
        ]
      }
    ],
    gerar: (ctx) => {
      const { dataVistoria, horaVistoria, decisao } = ctx.extras;
      const marca = (v) => (decisao === v ? 'X' : '&nbsp;&nbsp;');
      const hora = preenchido(horaVistoria) ? e(texto(horaVistoria)) : '____:____';
      return [
        titulo('DECLARAÇÃO SOBRE ACESSO AO NÚMERO IDENTIFICADOR DO MOTOR'),
        par(`${qualificacao(ctx.declarante)}.`),
        par('Proprietário, DECLARO, para os devidos fins, que, durante a vistoria do veículo abaixo identificado:'),
        veiculoLista(ctx.veiculo, ['PLACA', 'CHASSI', 'MODELO', 'RENAVAM']),
        par(`A ser realizada no dia ${e(dataCurta(dataVistoria))}, às ${hora}, (&nbsp;${marca('IREI')}&nbsp;) <strong>irei</strong> / (&nbsp;${marca('NAO_IREI')}&nbsp;) <strong>não irei</strong> realizar a remoção e/ou limpeza de componente(s) que estejam impossibilitando a visualização e acesso ao número identificador do motor do referido veículo.`),
        par('Estou ciente de que a correta visualização do número identificador do motor é essencial para a conclusão da vistoria e assumo total responsabilidade pela decisão acima assinalada conforme <strong>Portaria DETRAN/RS 475/15 POP 08-6.1.5 i)</strong>.'),
        par('Por ser verdade, firmo a presente declaração.'),
        fechamento(`Local: ${e(texto(ctx.local) || '______________________')}, Data: ${dataCurta(ctx.dataISO)}`, 'Assinatura do Proprietário')
      ].join('\n');
    }
  },
  {
    id: 'alteracao-cor',
    grupo: 'Declarações',
    titulo: 'Alteração de cor (mão de obra própria)',
    sub: 'Responsabilidade por pintura em outra cor feita pelo próprio proprietário',
    campos: [
      { chave: 'corAnterior', rotulo: 'Cor anterior', tipo: 'texto', placeholder: 'Em branco usa a cor do cadastro do veículo' },
      { chave: 'corNova', rotulo: 'Nova cor', tipo: 'texto', placeholder: 'Ex.: PRETA' }
    ],
    gerar: (ctx) => {
      const { corAnterior, corNova } = ctx.extras;
      const de = preenchido(corAnterior) ? corAnterior : ctx.veiculo.cor;
      return [
        titulo('DECLARAÇÃO DE RESPONSABILIDADE'),
        par(`${qualificacao(ctx.declarante)}, proprietário(a) do veículo ${veiculoFrase({ ...ctx.veiculo, cor: '' })}, declaro para os devidos fins, que o veículo acima citado sofreu alteração de cor de ${negrito(de)} para ${negrito(corNova)}, sendo o serviço efetuado com mão de obra própria, e fico civil e criminalmente responsável pelas informações prestadas.`),
        fechamento(localData(ctx), 'Declarante')
      ].join('\n');
    }
  },
  declaracaoNaoApresentacao({
    id: 'etiquetas-chapeamento-pintura',
    titulo: 'Etiquetas — chapeamento e pintura (mão de obra própria)',
    sub: 'Não apresentação das etiquetas de identificação após chapeamento e pintura',
    objeto: 'das etiquetas de identificação,',
    motivo: 'sendo que o veículo passou por serviços de chapeamento e pintura com mão de obra própria, sem necessidade de contratação de mão de obra especializada'
  }),
  // Veículo reprovado na vistoria e regularizado com peças que já eram do proprietário (apenas removidas e
  // reinstaladas): dispensa a nota fiscal de procedência. As peças são uma lista (botão + na tela).
  {
    id: 'procedencia-pecas',
    grupo: 'Declarações',
    titulo: 'Responsabilidade pela procedência de peças',
    sub: 'Veículo reprovado na vistoria, regularizado com peças próprias reinstaladas (sem nota fiscal)',
    rotuloDeclarante: 'Proprietário (declarante)',
    campos: [
      { chave: 'pecas', rotulo: 'Peças instaladas', tipo: 'lista', placeholder: 'Ex.: farol dianteiro direito', rotuloAdicionar: 'Adicionar peça' }
    ],
    gerar: (ctx) => {
      const pecas = (ctx.extras.pecas || []).map(texto).filter(Boolean);
      // Sem peças informadas, deixa linhas em branco para preencher à mão
      const itens = pecas.length ? pecas : ['', '', ''];
      const listaPecas = `<div class="veiculo">\n${itens.map((p) => `<div>&bull; ${p ? `<strong>${e(p)}</strong>` : CAMPO_EM_BRANCO + CAMPO_EM_BRANCO}</div>`).join('\n')}\n</div>`;
      const plural = pecas.length !== 1;
      return [
        titulo('DECLARAÇÃO DE RESPONSABILIDADE PELA PROCEDÊNCIA DE PEÇAS'),
        par(`${qualificacao(ctx.declarante)}, proprietário(a) do veículo ${veiculoFrase(ctx.veiculo)}, declaro, para os devidos fins, que o referido veículo, reprovado na vistoria, foi devidamente regularizado mediante a instalação ${plural ? 'das seguintes peças' : 'da seguinte peça'}:`),
        listaPecas,
        par(`Declaro, ainda, que ${plural ? 'as referidas peças já eram de minha propriedade e possuem procedência lícita, tendo sido apenas removidas do veículo e agora reinstaladas' : 'a referida peça já era de minha propriedade e possui procedência lícita, tendo sido apenas removida do veículo e agora reinstalada'}, razão pela qual não há necessidade de apresentação de nota fiscal de procedência.`),
        par('Por ser expressão da verdade, declaro-me civil, administrativa e criminalmente responsável pelas informações aqui prestadas.'),
        fechamento(localData(ctx), 'Proprietário (declarante)')
      ].join('\n');
    }
  },
  declaracaoNaoApresentacao({
    id: 'extravio-etiqueta-ano',
    titulo: 'Perda e extravio da etiqueta do ano (moto)',
    sub: 'Não apresentação da etiqueta de identificação do ano por extravio',
    objeto: 'da etiqueta de identificação do ano',
    motivo: MOTIVO_EXTRAVIO
  }),
  declaracaoNaoApresentacao({
    id: 'extravio-plaqueta',
    titulo: 'Extravio de plaqueta',
    sub: 'Não apresentação da plaqueta de identificação por extravio',
    objeto: 'da plaqueta de identificação',
    motivo: MOTIVO_EXTRAVIO
  }),
  declaracaoNaoApresentacao({
    id: 'extravio-plaqueta-bloco-motor',
    titulo: 'Extravio da plaqueta do bloco do motor',
    sub: 'Não apresentação da plaqueta do bloco do motor por extravio',
    objeto: 'da plaqueta do bloco do motor',
    motivo: MOTIVO_EXTRAVIO
  }),
  declaracaoNaoApresentacao({
    id: 'extravio-tampa-motor',
    titulo: 'Extravio da tampa do motor',
    sub: 'Não apresentação da tampa do motor por extravio',
    objeto: 'da tampa do motor',
    motivo: MOTIVO_EXTRAVIO
  }),
];

export const GRUPOS = ['Declarações'];

export const MODELOS_POR_ID = MODELOS.reduce((acc, m) => ({ ...acc, [m.id]: m }), {});

// Valores iniciais dos campos extras de um modelo
export function extrasIniciais(modelo) {
  return modelo.campos.reduce((acc, c) => {
    if (c.tipo === 'checks') acc[c.chave] = [];
    else if (c.tipo === 'lista') acc[c.chave] = [''];
    else if (c.tipo === 'valor') acc[c.chave] = null;
    else acc[c.chave] = c.padrao !== undefined ? c.padrao : '';
    return acc;
  }, {});
}

// Substitui {{TOKEN}} pelo valor correspondente — mesmo esquema simples usado em Procuração/Contrato.
function substituirTokens(texto, valores) {
  let resultado = texto;
  Object.entries(valores).forEach(([chave, valor]) => {
    resultado = resultado.split(`{{${chave}}}`).join(escapeHtml(valor || '')); // dado de cliente/veículo entra sempre escapado
  });
  return resultado;
}

// Tokens comuns a qualquer modelo (dados específicos de "extras" de cada modelo não viram token,
// já que variam muito de documento pra documento — mantemos só o que é sempre igual).
export function tokensComuns(ctx) {
  return {
    DECLARANTE: ctx.declarante?.nome || '',
    COMPRADOR: ctx.comprador?.nome || '',
    VEICULO_PLACA: (ctx.veiculo?.placa || '').toUpperCase(),
    VEICULO_CHASSI: (ctx.veiculo?.chassi || '').toUpperCase(),
    VEICULO_MARCA_MODELO: ctx.veiculo?.marcaModelo || '',
    LOCAL: ctx.local || '',
    DATA: dataCurta(ctx.dataISO)
  };
}

// ctx: { declarante, comprador, veiculo, extras, local, dataISO }
// textoCustomizado: texto salvo pela empresa em Configurações para este modelo (opcional) —
// quando presente, substitui o HTML padrão gerado por modelo.gerar(ctx).
export function gerarDocumentoHtml(modelo, ctx, textoCustomizado) {
  if (textoCustomizado) {
    return substituirTokens(textoCustomizado, tokensComuns(ctx));
  }
  return modelo.gerar(ctx);
}
